import { Dispatch, ReactNode, RefObject, SetStateAction, useEffect, useRef } from "react";

import useModalHandler from "../../hooks/useModalHandler.hooks";
import CancelIcon from "../../icons/cancel.icon";

interface ModalProps {
  setModalOpened: Dispatch<SetStateAction<boolean>>;
  btnRef?: RefObject<HTMLDivElement>;
  children: ReactNode;
  full?: boolean;
  variant?: "MEDIUM" | "LARGE";
  onclick?: () => void;
  skipClickOutside?: boolean;
}

export function PosModal({
  setModalOpened,
  btnRef,
  children,
  variant = "MEDIUM",
  onclick,
  skipClickOutside = false,
}: ModalProps) {
  const modalRef = useModalHandler(btnRef ?? null, setModalOpened, skipClickOutside);

  const handleOverLayClick = (e) => {
    if (skipClickOutside) return;

    const isModal = (modalRef.current as any).contains(e.target);

    if (!isModal) {
      setModalOpened(false);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setModalOpened(false);
        onclick && onclick();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [setModalOpened, onclick]);

  const modalClassName =
    "absolute top-1/2 right-1/2 bottom-0 left-0 w-[95%] z-[9999] " +
    "bg-white dark:text-white dark:bg-pos-dark rounded-2xl shadow-sm " +
    "top-1/2 right-1/2 -translate-y-1/2 translate-x-1/2 left-auto bottom-auto " +
    "w-full lg:w-[80vw] " +
    `${variant === "MEDIUM" ? " md:max-w-[600px]" : " md:max-w-[1000px] "}`;

  const handleModalClose = () => {
    setModalOpened(false);
    onclick && onclick();
  };

  return (
    <>
      <style jsx>{`
        ::-webkit-scrollbar {
          width: 10px;
          height: 10px;
          border-radius: 10px;
        }

        ::-webkit-scrollbar-track {
          background: inherit;
          border-radius: 10px;
        }

        ::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 5px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
      `}</style>
      <main
        id="overlay"
        className="fixed top-0 left-0 left right-0 bottom-0 bg-black bg-opacity-80 backdrop-filter backdrop-blur-sm z-[9999]"
        onClick={handleOverLayClick}
      >
        <div ref={modalRef} className={modalClassName}>
          <button
            id="modal-close-btn"
            onClick={handleModalClose}
            className="absolute right-2 top-2 md:-right-16 md:-top-0 flex flex-col justify-center items-center"
          >
            <span className="bg-inherit md:bg-white flex justify-center items-center w-12 h-12 py-3 px-2 rounded-[50%]">
              <CancelIcon className="w-8 h-8 stroke-pos-black-blue dark:stroke-white dark:md:stroke-pos-black-blue" />
            </span>
            <span className="hidden md:inline text-sm text-white">ESC</span>
          </button>
          <section className="p-2">
            <div className="max-h-[85vh] py-12 overflow-auto">{children}</div>
          </section>
        </div>
      </main>
    </>
  );
}
